import type {
  TypePolicies,
  FieldPolicy,
  FieldFunctionOptions,
  Operation,
  NextLink,
  FetchResult,
  NormalizedCacheObject,
} from '@apollo/client';
import { ApolloClient, ApolloLink, InMemoryCache, from, HttpLink } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import type { ErrorResponse } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { relayStylePagination } from '@apollo/client/utilities';
import ActionCableLink from 'graphql-ruby-client/subscriptions/ActionCableLink';
import { createConsumer } from '@rails/actioncable';
import { environment, appHost, subdomain } from '~/constantDefinitions';
import { relayStylePaginationScopedMedias } from '~/singlePageApp/Apollo/relayStylePaginationScopedMedias';
import connectionTypes from '../../app/graphql/connectionTypes.json';
import possibleTypes from '../../app/graphql/possibleTypes.json';

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const RETRY_INITIAL_DELAY = 300;
const MAX_RETRY_ATTEMPTS = 5;

declare global {
  interface Window {
    _auth_token: string;
    _auth_token_name: string;
    apolloRedirectingDueToLogout?: boolean;
  }
}

interface ApolloClientCallbacks {
  onSuccess?: () => void;
  onNetworkError?: (networkError: Error) => void;
}

// Define schema types
type SchemaType = 'AnonymousSchema' | 'GladminSchema' | 'WistiaSchema' | undefined;

// Define options type for Apollo Client
interface ApolloClientOptions {
  [key: string]: unknown;
  tokenFetcher?: () => string | null | undefined;
}

// Define edge policy type
interface EdgeWithNode {
  node: Record<string, unknown>;
}

interface NetworkErrorWithResult extends Error {
  statusCode?: number;
  result?: {
    redirect_to?: string;
  };
}

const abortController = new AbortController();
const { signal } = abortController;

const edgesFieldPolicy: FieldPolicy = {
  read: (existingEdges: EdgeWithNode[] | undefined, { canRead }: FieldFunctionOptions) =>
    existingEdges?.filter((edge: EdgeWithNode) => canRead(edge.node)) ?? [],
};

const defaultConnectionPolicy: TypePolicies[string] = {
  fields: { edges: edgesFieldPolicy },
};

const defaultConnectionTypePolicies: TypePolicies = connectionTypes.data.reduce(
  (policies: TypePolicies, type: string) => ({ ...policies, [type]: defaultConnectionPolicy }),
  {},
);

// returns relayStylePagination helper methods when the
// field alias matches the given alias
const relayStylePaginationForFieldAlias = (alias: string): FieldPolicy => ({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  keyArgs(args: any, context: any): any {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (context.field?.alias?.value === alias) {
      return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return Object.keys(args ?? {});
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  read(existing: any, options: any): any {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (options.field?.alias?.value === alias) {
      const pagination = relayStylePagination();
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return pagination.read ? pagination.read(existing, options) : existing;
    }

    return existing;
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  merge(existing: any, incoming: any, options: any): any {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (options.field?.alias?.value === alias) {
      const pagination = relayStylePagination();
      return typeof pagination.merge === 'function'
        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          pagination.merge(existing, incoming, options)
        : incoming;
    }

    return incoming;
  },
});

export const makeCache = (): InMemoryCache =>
  new InMemoryCache({
    possibleTypes,
    addTypename: true,
    dataIdFromObject: (obj: { id: string }): string => obj.id,
    typePolicies: {
      ...defaultConnectionTypePolicies,
      Account: {
        fields: {
          projectSearch: {
            keyArgs: ['filters', 'search'],
            merge(_existing: unknown, incoming: unknown[] = []): unknown[] {
              return incoming;
            },
          },

          projectSearchByConnection: relayStylePaginationForFieldAlias('infiniteProjects'),

          allContentItems: relayStylePaginationForFieldAlias('infiniteItems'),

          archivedItems: relayStylePagination(),

          medias: relayStylePagination(),

          projects: relayStylePagination(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (_args: any, context: any): any => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              if (context.field?.alias?.value === 'infiniteProjects') {
                return false;
              }

              return ['filters', 'orderBy', 'after', 'before', 'first', 'last'];
            },
          ),

          favoritedAllContentItems: relayStylePagination(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (_args: any, context: any): any => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              if (context.field?.alias?.value === 'infiniteFavorites') {
                return false;
              }

              return ['filters', 'orderBy', 'after', 'before', 'first', 'last'];
            },
          ),

          videos: relayStylePaginationForFieldAlias('infiniteVideos'),

          channels: relayStylePaginationForFieldAlias('infiniteChannels'),

          liveStreamEvents: relayStylePagination(['ended']),

          tags: relayStylePagination(),

          ownersAndManagers: relayStylePagination(),

          mediaRequests: relayStylePagination(),

          featureAccess: {
            // Equivalent to options.mergeObjects(existing, incoming).
            merge: true,
          },

          billingInvoices: relayStylePagination(),
        },
      },
      Media: {
        fields: {
          topLevelTeamComments: relayStylePagination(),
          topLevelAudienceComments: relayStylePagination(),
        },
      },
      CurrentStatus: {
        merge: (_: unknown, incoming: unknown): unknown => incoming,
      },
      AuthorizationResult: {
        merge: (_: unknown, incoming: unknown): unknown => incoming,
      },
      AuthorizationReason: {
        merge: (_: unknown, incoming: unknown): unknown => incoming,
      },
      AnonymousMediaGroup: {
        fields: {
          medias: relayStylePagination(),
        },
      },
      AnonymousProject: {
        fields: {
          mediaGroups: relayStylePagination(),
        },
      },
      AnonymousMedia: {
        fields: {
          topLevelTeamComments: relayStylePagination(),
          topLevelAudienceComments: relayStylePagination(),
        },
      },
      ChannelSection: {
        fields: {
          episodes: relayStylePagination(),
        },
      },
      Channel: {
        fields: {
          channelRoles: relayStylePagination(),
          medias: relayStylePagination(),
          sections: relayStylePagination(),
          channelEpisodes: {
            // Equivalent to options.mergeObjects(existing, incoming).
            merge: true,
          },
          embedOptions: {
            merge(_existing: unknown, incoming: unknown = null): unknown {
              return incoming;
            },
          },
        },
      },
      MediaGroup: {
        fields: {
          medias: relayStylePaginationScopedMedias(),
        },
      },
      Project: {
        fields: {
          mediaGroups: relayStylePagination(['untitled']),
          medias: relayStylePaginationScopedMedias(),
          permissionsForCurrentContact: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            merge(_existing: any, incoming: any = null): any {
              return { ..._existing, ...incoming };
            },
          },
        },
      },
      PodcastImportRequest: {
        fields: {
          podcastFileImportsWhereFailedOrMediaFailed: relayStylePagination(),
          parsedChannel: {
            merge(_existing: unknown, incoming: unknown = null): unknown {
              return incoming;
            },
          },
        },
      },
      Contact: {
        fields: {
          containersWithAdminPermissions: relayStylePagination(),
          channelsWithAdminPermissions: relayStylePagination(),
          projectsWithAdminPermissions: relayStylePagination(),
          accountsForSelection: relayStylePagination([
            'includeAgencySubAccounts',
            'after',
            'before',
            'first',
            'last',
          ]),
          conversations: relayStylePagination(),
          notifications: relayStylePagination(['filters']),
          ownedMediasWithReplaceVideos: relayStylePagination(),
          teamAccounts: {
            merge: true,
          },
          permissions: {
            merge: true,
          },
        },
      },
      Permissions: {
        keyFields: false,
      },
      FeatureAccess: {
        keyFields: false,
      },
      TeamComment: {
        fields: {
          teamReplies: relayStylePagination(),
        },
      },
      AudienceComment: {
        fields: {
          audienceReplies: relayStylePagination(),
        },
      },
      ChannelRole: {
        fields: {
          contact: {
            merge(_existing: unknown, incoming: unknown): unknown {
              return incoming;
            },
          },
        },
      },
      ProjectRole: {
        fields: {
          contact: {
            merge(_existing: unknown, incoming: unknown): unknown {
              return incoming;
            },
          },
        },
      },
      CreateMediaData: {
        merge(_existing: unknown, incoming: unknown): unknown {
          return incoming;
        },
      },
      UploadStatusData: {
        merge(_existing: unknown, incoming: unknown): unknown {
          return incoming;
        },
      },
      ReviewLinkMedia: {
        fields: {
          topLevelTeamComments: relayStylePagination(),
        },
      },
      AppMetadata: {
        merge(
          existing: Record<string, unknown> | undefined,
          incoming: Record<string, unknown>,
        ): Record<string, unknown> {
          return { ...existing, ...incoming };
        },
      },
    },
  });

const getLinks = (
  callbacks: ApolloClientCallbacks,
  schema: SchemaType,
  opts: ApolloClientOptions,
): ApolloLink => {
  const headers: Record<string, string> = { 'X-CSRF-TOKEN': window._auth_token };

  if (schema) {
    headers['X-WISTIA-GQL-SCHEMA'] = schema;
  }

  /*
   * The expiringAccessToken stored in the sessionStorage can be changed
   * asynchronously and without redrawing the ApolloProvider which is problematic. For this
   * reason we need a custom fetch which can check these values on every graphql query. Doing
   * this the normal way would store stale values.
   */
  const tokenAwareFetch = async (uri: string, passedOptions: RequestInit): Promise<Response> => {
    const token = opts.tokenFetcher?.() ?? undefined;
    const options: RequestInit = { ...passedOptions };

    options.signal = signal;

    options.credentials = opts.tokenFetcher ? 'omit' : 'include';
    if (opts.tokenFetcher && typeof token === 'string') {
      options.headers ??= {};
      (options.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(uri, options);
  };

  // We don't want to route graqphql requests through fastly. For the transcript embed
  // we detect if it's a fast URL and change the requests to route through app.
  const appHostIfNeeded = subdomain === 'fast' ? `//${appHost}` : '';
  const httpLink = new HttpLink({
    uri: ({ operationName }: { operationName?: string }) =>
      `${appHostIfNeeded}/graphql?op=${operationName ?? ''}`,
    fetch: tokenAwareFetch,
    headers,
  });

  const hasSubscriptionOperation = (operation: Operation): boolean => {
    const { definitions } = operation.query;
    return definitions.some(
      (def: { kind: string; operation?: string }) =>
        def.kind === 'OperationDefinition' && def.operation === 'subscription',
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const cable = createConsumer();

  const link = ApolloLink.split(
    hasSubscriptionOperation,

    new ActionCableLink({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      cable,
      channelName: 'GraphqlChannel',
      actionName: 'execute',
      connectionParams: {},
    }),
    httpLink,
  );

  const successLink = new ApolloLink((operation: Operation, forward: NextLink) =>
    forward(operation).map((response: FetchResult) => {
      if (!response.errors) {
        callbacks.onSuccess?.();
      }

      return response;
    }),
  );

  const errorLink = onError(
    ({ graphQLErrors, networkError, forward, operation }: ErrorResponse) => {
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations = [], path }) => {
          const firstLocation = locations[0];
          // eslint-disable-next-line no-console
          console.error(
            `[GraphQL error]: Message: ${message}, line: ${firstLocation.line || 'unknown'}, Column: ${firstLocation.column || 'unknown'}, Path: ${String(path)}`,
          );
        });
      }

      if (networkError) {
        // eslint-disable-next-line no-console
        console.error('[Network error]:', networkError);

        const typedNetworkError = networkError as NetworkErrorWithResult;

        if (
          (typedNetworkError.statusCode === HTTP_FORBIDDEN ||
            typedNetworkError.statusCode === HTTP_UNAUTHORIZED) &&
          window.apolloRedirectingDueToLogout !== true
        ) {
          const { result } = typedNetworkError;
          const redirectURL: URL = (() => {
            if (
              result &&
              typeof result === 'object' &&
              'redirect_to' in result &&
              typeof result.redirect_to === 'string'
            ) {
              return new URL(result.redirect_to, window.location.origin);
            }
            const redirectTo = encodeURIComponent(window.location.toString());
            return new URL(`/login?redirect_to=${redirectTo}`, window.location.origin);
          })();

          // Not doing this leads to CSRF issues due to in flight requests clobbering the
          // state that is set by omniauth. This is because those requests return after
          // omniauth has set the state in the cookie but then the cookie value gets clobbered
          // by those requests. Once the user logs in there is no state for omniauth.
          // If you change the text of this abort, please update frontend/datadogRum/initializeSentryClient.ts
          abortController.abort('Redirecting to login page');

          window.apolloRedirectingDueToLogout = true;
          window.location.assign(redirectURL);
          return forward(operation);
        }

        callbacks.onNetworkError?.(networkError);
      }

      return forward(operation);
    },
  );

  // Only retries for 401's
  // buys some time to avoid an error page, as window.setlocation is async
  const retryLink = new RetryLink({
    delay: {
      initial: RETRY_INITIAL_DELAY,
      max: Infinity,
      jitter: true,
    },
    attempts: {
      max: MAX_RETRY_ATTEMPTS,
      retryIf: (_error: Error, operation: Operation) => {
        const context = operation.getContext();
        const response = context.response as { status?: number } | undefined;
        return response?.status === HTTP_UNAUTHORIZED;
      },
    },
  });

  return from([successLink, retryLink, errorLink, link]);
};

/**
 * @param opts Options to pass to the apollo client
 * @param callbacks Callbacks to pass to ApolloLink
 * @param schema the schema to access
 * @returns ApolloClient
 */
export const getApolloClient = (
  opts: ApolloClientOptions = {},
  callbacks: ApolloClientCallbacks = {},
  schema: SchemaType = undefined,
): ApolloClient<NormalizedCacheObject> => {
  const headers: Record<string, string> | undefined = schema
    ? { 'X-WISTIA-GQL-SCHEMA': schema }
    : undefined;

  return new ApolloClient({
    ...opts,
    link: getLinks(callbacks, schema, opts),
    connectToDevTools: environment === 'development',
    cache: makeCache(),
    headers,
  });
};
